"use client";

import * as React from "react";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PhosphorIcon } from "@/components/marketing/PhosphorIcon";
import type {
  ITeamMember,
  ITeamSocialLink,
  TTeamSocialLinkType,
} from "@/lib/sanity/types";

const BIO_PREVIEW_LENGTH = 40;

const ICON_BY_TYPE: Record<TTeamSocialLinkType, string> = {
  github: "github-logo",
  facebook: "facebook-logo",
  instagram: "instagram-logo",
  website: "globe",
  linkedin: "linkedin-logo",
  tiktok: "tiktok-logo",
  x: "x-logo",
};

function socialAria(memberName: string, link: ITeamSocialLink): string {
  if (link.type === "website" && link.label?.trim()) {
    return `${memberName} — ${link.label}`;
  }
  return `${memberName} — ${link.type}`;
}

function SocialIconStrip({
  memberName,
  links,
}: {
  memberName: string;
  links: NonNullable<ITeamMember["socialLinks"]>;
}): React.ReactElement {
  return (
    <div className="mt-4 flex flex-wrap justify-center gap-2">
      {links.map((link) => {
        const iconName = ICON_BY_TYPE[link.type];
        const aria = socialAria(memberName, link);
        const label =
          link.type === "website"
            ? (link.label?.trim() ?? "Website")
            : link.type;

        return (
          <Button
            key={link._key}
            asChild
            variant="secondary"
            size="sm"
            className="gap-2 rounded-md px-3 py-2 text-xs"
          >
            <Link
              href={link.url}
              target="_blank"
              rel="noreferrer"
              aria-label={aria}
            >
              <PhosphorIcon
                name={iconName}
                className="h-4 w-4"
                aria-hidden="true"
              />
              <span className="capitalize">{label}</span>
            </Link>
          </Button>
        );
      })}
    </div>
  );
}

export interface ITeamMemberCardProps {
  member: ITeamMember;
  imageUrl: string | null;
}

/**
 * Circular team portrait with truncated bio, social link chips, and profile dialog.
 */
export function TeamMemberCard({
  member,
  imageUrl,
}: ITeamMemberCardProps): React.ReactElement {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const socialLinks = member.socialLinks ?? [];
  const bio = member.bio?.trim() ?? "";
  const bioNeedsTruncate = bio.length > BIO_PREVIEW_LENGTH;
  const bioPreview = bioNeedsTruncate
    ? `${bio.slice(0, BIO_PREVIEW_LENGTH)}…`
    : bio;

  return (
    <>
      <article className="group mx-auto flex max-w-xs flex-col items-center text-center">
        <div className="relative mx-auto mb-4 w-fit transition-[transform,box-shadow] duration-300 motion-reduce:transition-none motion-safe:group-hover:-translate-y-1 motion-safe:group-hover:scale-[1.02] motion-reduce:group-hover:translate-y-0 motion-reduce:group-hover:scale-100">
          <div className="rounded-full bg-gradient-to-br from-border via-border to-border p-[2px] transition-all duration-300 motion-reduce:transition-none group-hover:from-primary/45 group-hover:via-primary/25 group-hover:to-primary/10 group-hover:shadow-lg group-hover:shadow-primary/15">
            <div className="relative aspect-square w-48 overflow-hidden rounded-full border border-border/80 bg-muted md:w-56">
              {imageUrl ? (
                <Image
                  src={imageUrl}
                  alt={`${member.name} headshot`}
                  fill
                  sizes="(min-width: 768px) 224px, 192px"
                  className="object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <PhosphorIcon
                    name="user"
                    className="h-16 w-16 text-muted-foreground md:h-20 md:w-20"
                    aria-hidden="true"
                  />
                </div>
              )}

              <div
                className="pointer-events-none absolute inset-x-0 bottom-0 top-1/2 bg-gradient-to-t from-background/40 via-transparent to-transparent"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>

        <h3 className="text-xl font-semibold tracking-tight text-foreground">
          {member.name}
        </h3>
        <p className="mt-1 text-sm font-semibold text-primary">
          {member.title}
        </p>

        {bio ? (
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            {bioPreview}
            {bioNeedsTruncate ? (
              <>
                {" "}
                <button
                  type="button"
                  className="font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  onClick={() => setDialogOpen(true)}
                >
                  Show more
                </button>
              </>
            ) : null}
          </p>
        ) : null}

        {socialLinks.length > 0 ? (
          <SocialIconStrip memberName={member.name} links={socialLinks} />
        ) : null}
      </article>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md sm:max-w-lg">
          <DialogHeader className="items-center text-center sm:items-center">
            <div className="relative mx-auto mb-4 h-28 w-28 overflow-hidden rounded-full border border-border bg-muted">
              {imageUrl ? (
                <Image
                  src={imageUrl}
                  alt={`${member.name} headshot`}
                  fill
                  sizes="112px"
                  className="object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <PhosphorIcon
                    name="user"
                    className="h-10 w-10 text-muted-foreground"
                    aria-hidden="true"
                  />
                </div>
              )}
            </div>
            <DialogTitle>{member.name}</DialogTitle>
            <p className="text-sm font-semibold text-primary">{member.title}</p>
          </DialogHeader>
          {bio ? (
            <DialogDescription asChild>
              <p className="text-center text-sm leading-relaxed text-muted-foreground">
                {bio}
              </p>
            </DialogDescription>
          ) : null}
          {socialLinks.length > 0 ? (
            <SocialIconStrip memberName={member.name} links={socialLinks} />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
